import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Breadcrumbs as MuiBreadcrumbs,
} from "@mui/material";
import {
  SyncAlt as DateSwapIcon,
  FilterList as FilterIcon,
  CalendarTodayOutlined as CalendarIcon,
} from "@mui/icons-material";
import { ChevronRight } from "lucide-react";
import { colors, borderRadiusPx } from "../../../../theme";
import { CommonButton, CommonMenu } from "../../ui";

/**
 * DashboardHeader - Top header with title, subtitle, date range selector, and filter controls.
 */
const DashboardHeader = ({
  title = "",
  subtitle = "",
  breadcrumbs,
  dateRangeText = "",
  onDateRangeChange,
  onFilterClick,
  actionButton,
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClickDate = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (range) => {
    setAnchorEl(null);
    if (range && typeof onDateRangeChange === "function") {
      onDateRangeChange(range);
    }
  };

  const renderBreadcrumbs = () => {
    if (!breadcrumbs) return null;

    if (Array.isArray(breadcrumbs)) {
      return (
        <MuiBreadcrumbs
          separator={
            <ChevronRight size={14} style={{ color: colors.text.muted }} />
          }
          aria-label="breadcrumb"
          sx={{
            mb: 1.25,
            "& .MuiBreadcrumbs-separator": {
              mx: 0.75,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;

            if (React.isValidElement(item)) {
              return <React.Fragment key={index}>{item}</React.Fragment>;
            }

            if (typeof item === "string") {
              return (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "13px",
                    fontWeight: isLast ? 600 : 500,
                    color: isLast ? colors.text.primary : colors.text.secondary,
                  }}
                >
                  {item}
                </Typography>
              );
            }

            if (item && typeof item === "object") {
              const { label, href, onClick, icon } = item;

              if (href && !isLast) {
                return (
                  <Box
                    key={index}
                    component={Link}
                    to={href}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: colors.text.secondary,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      transition: "color 0.15s ease",
                      "&:hover": {
                        color: colors.primary.main,
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              }

              if (onClick && !isLast) {
                return (
                  <Box
                    key={index}
                    component="button"
                    onClick={onClick}
                    sx={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: colors.text.secondary,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      transition: "color 0.15s ease",
                      "&:hover": {
                        color: colors.primary.main,
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              }

              return (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "13px",
                    fontWeight: isLast ? 600 : 500,
                    color: isLast ? colors.text.primary : colors.text.secondary,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {icon}
                  {label}
                </Typography>
              );
            }

            return null;
          })}
        </MuiBreadcrumbs>
      );
    }

    return <Box sx={{ mb: 1 }}>{breadcrumbs}</Box>;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: 2,
        mb: 3,
        ...sx,
      }}
    >
      {/* Title & Subtitle */}
      <Box sx={{ textAlign: "left" }}>
        {renderBreadcrumbs()}

        {title && (
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: colors.text.title,
              fontSize: { xs: "22px", md: "26px" },
              letterSpacing: "-0.5px",
              mb: 0.5,
              textAlign: "left",
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: "14px",
              textAlign: "left",
              lineHeight: 1.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Right Controls: Date Range + Filters */}
      {(dateRangeText || onFilterClick || actionButton) && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          {dateRangeText && (
            <>
              <Paper
                elevation={0}
                onClick={handleClickDate}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2,
                  py: 0.9,
                  borderRadius: borderRadiusPx.lg,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  backgroundColor: colors.dashboard.cardBg,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: colors.dashboard.cardBorderHover,
                    backgroundColor: colors.slate[50],
                  },
                }}
              >
                <CalendarIcon sx={{ fontSize: 18, color: colors.slate[500] }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.83rem",
                    color: colors.slate[700],
                  }}
                >
                  {dateRangeText}
                </Typography>
                <DateSwapIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
              </Paper>

              <CommonMenu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                width={180}
              >
                <Box sx={{ p: 1 }}>
                  {[
                    "Today",
                    "This Week",
                    "This Month",
                    "This Quarter",
                    "Year to Date",
                    "All Time",
                  ].map((range) => (
                    <Box
                      key={range}
                      onClick={() => handleClose(range)}
                      sx={{
                        p: "8px 12px",
                        borderRadius: borderRadiusPx.sm,
                        cursor: "pointer",
                        fontSize: "0.83rem",
                        fontWeight: dateRangeText === range ? 700 : 500,
                        color:
                          dateRangeText === range
                            ? colors.primary.main
                            : colors.slate[700],
                        backgroundColor:
                          dateRangeText === range
                            ? colors.primary.lighter
                            : "transparent",
                        "&:hover": {
                          backgroundColor: colors.slate[100],
                        },
                      }}
                    >
                      {range}
                    </Box>
                  ))}
                </Box>
              </CommonMenu>
            </>
          )}

          {onFilterClick && (
            <CommonButton
              variant="outline"
              size="medium"
              startIcon={<FilterIcon sx={{ fontSize: 18 }} />}
              onClick={onFilterClick}
            >
              Filters
            </CommonButton>
          )}

          {actionButton}
        </Stack>
      )}
    </Box>
  );
};

DashboardHeader.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  breadcrumbs: PropTypes.node,
  dateRangeText: PropTypes.string,
  onDateRangeChange: PropTypes.func,
  onFilterClick: PropTypes.func,
  actionButton: PropTypes.node,
  sx: PropTypes.object,
};

export default DashboardHeader;
